import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, TeamDot } from "./Ui";
import { teamShortName } from "../metrics";
import { avatarSrc } from "../hooks/useBdApi";

/**
 * Global search over teams and members (from /meta). Navigates on select.
 */
export default function SearchBox({ meta, search, basePath = "/landmark/bd" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const ref = useRef(null);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !meta) return [];
    const teams = (meta.teams || [])
      .filter((t) => t.name.toLowerCase().includes(needle))
      .map((t) => ({ kind: "team", id: t.key, label: t.name, sub: `${t.memberCount} members`, team: t.name }));
    const members = (meta.members || [])
      .filter((m) => m.name.toLowerCase().includes(needle) || (m.email || "").toLowerCase().includes(needle))
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name))
      .slice(0, 8)
      .map((m) => ({ kind: "member", id: m.sfUserId, label: m.name, sub: `${m.title ? `${m.title} · ` : ""}${teamShortName(m.team)}${m.isActive ? "" : " · inactive"}`, team: m.team, user: m }));
    return [...teams, ...members];
  }, [q, meta]);

  useEffect(() => setIndex(0), [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const go = (r) => {
    if (!r) return;
    navigate(r.kind === "team" ? `${basePath}/team/${r.id}${search}` : `${basePath}/member/${r.id}${search}`);
    setQ("");
    setOpen(false);
  };

  return (
    <div className="bd-search" ref={ref}>
      <svg className="bd-search__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        placeholder="Search teams or people…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setIndex((i) => Math.min(results.length - 1, i + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setIndex((i) => Math.max(0, i - 1));
          } else if (e.key === "Enter") {
            go(results[index]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && q.trim() && (
        <div className="bd-search__results">
          {!results.length ? (
            <div className="bd-search__empty">No matches</div>
          ) : (
            results.map((r, i) => (
              <button
                key={`${r.kind}-${r.id}`}
                type="button"
                className={`bd-search__result ${i === index ? "is-active" : ""}`}
                onMouseEnter={() => setIndex(i)}
                onClick={() => go(r)}
              >
                {r.kind === "team" ? <TeamDot team={r.team} /> : <Avatar name={r.label} team={r.team} size="sm" src={avatarSrc(r.user)} />}
                <span className="bd-search__result-main">
                  <span>{r.label}</span>
                  <small>{r.sub}</small>
                </span>
                <span className="bd-search__kind">{r.kind}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
